import { get_state } from 'cache-store';

import has from '@dojo/framework/core/has';
import { ParentTree } from '@holmescorp/lcc-interfaces';
import { logUser } from '@holmescorp/logger';
import { publish } from '@holmescorp/pub-sub';

import { ditarootsChange, topicsChange, viewChange } from '../LccStore';
import { locationChange, recommendedChange } from '../UserState';
import { TopicDef, TopicRoots } from '../interfaces';
import { svc } from './booksvc';
import { cache_all_topics } from './loadtopic';

export interface PathTitle {
	path: string;
	title: string;
	id?: string;
}

export interface PathmapData {
	topic: TopicDef;
	view: () => void;
}

export interface TopicQuiz {
	quiz: string | null;
	flashcards: string | null;
	discussion: string | null;
	casestudies: string[];
	calculations: string[];
	exercises: string[];
	titles: { [key: string]: string };
	[key: string]: string | string[] | null | { [key: string]: string };
}

export const childmap = new Map<string, string[]>();
export const pathmap = new Map<string, PathmapData>();
export const keymap = new Map<string, PathTitle>();
export const tgfkeys = new Set<string>();
export const quizmap = new Map<string, TopicQuiz>();
export const bonusquizzes = new Map<string, PathTitle>();

const emptyTopicQuiz: TopicQuiz = {
	quiz: null,
	flashcards: null,
	discussion: null,
	casestudies: [],
	calculations: [],
	exercises: [],
	titles: {}
};

function calcPaths(rootname: string, topicdef: TopicDef): Promise<TopicDef> {
	return new Promise((resolve) => {
		const { title, id } = topicdef.topichead.attributes!;
		// use id (in english) if available, else fallback to rootname
		const thslug = id || rootname;
		const thpath = `/explore/${thslug}`;
		pathmap.set(thpath, {
			topic: topicdef,
			view: () => {
				//
			}
		});
		keymap.set(rootname, { path: thpath, title, id });
		childmap.set(rootname, []);

		const { children: thkids = [] } = topicdef.topichead;
		thkids.forEach((thkid, idx) => {
			if ('topicset' in thkid) {
				const { key, title } = thkid.topicset.attributes!;
				const tsetpath = `${thpath}/${key}`;
				tgfkeys.add(key);
				quizmap.set(key, { ...emptyTopicQuiz });
				const view = () => {
					locationChange({ location: tsetpath });
					// tapaneChange({ accordionSelected: key });
					if (has('use-micro-view')) {
						viewChange({
							view: {
								widget: 'MicroTopicModule',
								params: {
									topic: { ...thkid.topicset },
									expandKey: key
								}
							}
						});
					} else {
						viewChange({
							view: {
								widget: 'TopicAccordion',
								params: {
									topic: { ...topicdef.topichead },
									expandKey: key
								}
							}
						});
					}
				};
				if (idx === 0) {
					pathmap.set(thpath, { topic: topicdef, view });
					const { title, id } = keymap.get(rootname)!;
					keymap.set(rootname, { title, path: tsetpath, id });
				}
				pathmap.set(tsetpath, { topic: thkid, view });
				keymap.set(key, { path: tsetpath, title });
				childmap.set(key, []);
				childmap.get(rootname)!.push(key);
				const tsname = key;

				const { children: tsetkids = [] } = thkid.topicset;
				tsetkids.forEach((tskid) => {
					if ('topicgroup' in tskid) {
						const { key, title } = tskid.topicgroup.attributes!;
						const tgpath = `${tsetpath}/${key}`;
						const view = () => {
							const { attributes, children } = tskid.topicgroup;
							locationChange({ location: tgpath });
							if (has('use-micro-view')) {
								viewChange({
									view: {
										widget: 'MicroTopicGroup',
										params: { topic: { attributes, children }, parent: { ...thkid.topicset } }
									}
								});
							} else if (has('use-rise')) {
								viewChange({
									view: {
										widget: 'RiseTopicPage',
										params: { rel: key, title, key: `rise-${key}` }
									}
								});
							} else if (has('shrm2024') || has('use-topicgroup-reading')) {
								viewChange({
									view: {
										widget: 'TopicFull',
										params: {
											topic: { attributes, children }
										}
									}
								});
							} else {
								viewChange({
									view: {
										widget: 'TopicGroup',
										params: {
											topic: { attributes, children }
										}
									}
								});
							}
						};
						pathmap.set(tgpath, { topic: tskid, view });
						keymap.set(key, { path: tgpath, title });
						childmap.set(key, []);
						childmap.get(tsname)!.push(key);
						quizmap.set(key, { ...emptyTopicQuiz });
						const tgname = key;

						const { children: tgkids = [] } = tskid.topicgroup;
						tgkids.forEach((tgkid) => {
							if ('topicref' in tgkid) {
								const { attributes, children } = tgkid.topicref;
								const { rel, title } = attributes!;
								const topicpath = `${tgpath}/${rel}`;
								const view = () => {
									locationChange({ location: topicpath });
									if (has('use-micro-view')) {
										viewChange({
											view: {
												widget: 'MicroTopicSingle',
												params: {
													topic: { attributes, children },
													parent: { ...thkid.topicset }
												}
											}
										});
									} else {
										viewChange({
											view: {
												widget:
													has('shrm2024') || has('use-topicgroup-reading')
														? 'TopicSingle'
														: 'TopicPage',
												params: { rel, title, key: '' }
											}
										});
									}
								};

								pathmap.set(topicpath, { topic: tgkid, view });
								keymap.set(rel as string, { path: topicpath, title });
								childmap.get(tgname)!.push(rel as string);
								quizmap.set(rel as string, { ...emptyTopicQuiz });
							}
						});
					}
				});
			}
		});
		resolve(topicdef);
	});
}

async function setup_quizzes(): Promise<void> {
	const quizids = await svc.quiz_list();
	Object.keys(quizids)
		.sort()
		.forEach((id) => {
			if (/-flashcards|-discussion|-casestudy-[0-9]+|-calculation-[0-9]+|-exercise-[0-9]+/.test(id)) {
				const topic = id.replace(
					/-flashcards|-discussion|-casestudy-[0-9]+|-calculation-[0-9]+|-exercise-[0-9]+/,
					''
				);
				// get the chunk after the topic, i.e "-casestudy-1" from "topic2021-1-casestudy-1"
				const typeid = id.split(topic).join('').substring(1);
				const type = typeid.split('-')[0];
				const entry = quizmap.get(topic);
				if (entry && type) {
					entry['titles'][id] = quizids[id];
					switch (type) {
						case 'casestudy':
							entry['casestudies'] = [...entry['casestudies'], id];
							break;
						case 'calculation':
							entry['calculations'] = [...entry['calculations'], id];
							break;
						case 'exercise':
							entry['exercises'] = [...entry['exercises'], id];
							break;
						default:
							entry[type] = id;
							break;
					}
					quizmap.set(topic, entry);
					const { path, title } = keymap.get(topic) || {};
					if (path) {
						const fcpath = `${path}/${typeid}`;
						pathmap.set(fcpath, {
							topic: {},
							view: () => {
								locationChange({ location: fcpath });
								viewChange({
									view: {
										widget: 'quiz',
										params: { topicid: id, title }
									}
								});
							}
						});
					}
				}
			} else if (id.startsWith('topic')) {
				const topic = id.split(/-s?cp$/)[0];
				const entry = quizmap.get(topic);
				if (entry) {
					entry.quiz = topic;
					quizmap.set(topic, entry);
					const { path, title } = keymap.get(topic) || {};
					if (path) {
						const quizpath = `${path}/quiz`;
						pathmap.set(quizpath, {
							topic: {},
							view: () => {
								locationChange({ location: quizpath });
								viewChange({
									view: {
										widget: 'quiz',
										params: { topicid: topic, title }
									}
								});
							}
						});
					}
				}
			} else if (id.startsWith('bonus')) {
				const title = quizids[id];
				const path = `/bonus-quiz/${id}`;
				quizmap.set(id, {
					quiz: id,
					flashcards: null,
					discussion: null,
					casestudies: [],
					calculations: [],
					exercises: [],
					titles: {}
				});
				keymap.set(id, { path, title });
				bonusquizzes.set(id, { path, title });
				pathmap.set(path, {
					topic: {},
					view: () => {
						locationChange({ location: path });
						viewChange({
							view: {
								widget: 'quiz',
								params: { topicid: id, title }
							}
						});
					}
				});
			} else if (/pre-test/.test(id)) {
				const quiz = id.split(/-s?cp$/)[0];
				quizmap.set(quiz, {
					quiz,
					flashcards: null,
					discussion: null,
					casestudies: [],
					calculations: [],
					exercises: [],
					titles: {}
				});
				const title = quizids[id] || '';
				keymap.set(quiz, { path: `/${quiz}`, title });
				pathmap.set(`/${quiz}`, {
					topic: {},
					view: () => {
						publish('site/assessment', quiz);
						// publish(`site/${id.split('-').join('/')}`);
					}
				});
			} else if (/practice/.test(id)) {
				quizmap.set('practice', {
					quiz: id.split(/-s?cp$/)[0],
					flashcards: null,
					discussion: null,
					casestudies: [],
					calculations: [],
					exercises: [],
					titles: {}
				});
			} else if (/post-test/.test(id)) {
				quizmap.set('posttest', {
					quiz: id.split(/-s?cp$/)[0],
					flashcards: null,
					discussion: null,
					casestudies: [],
					calculations: [],
					exercises: [],
					titles: {}
				});
			}
		});
	return;
}

export async function get_topic_roots(version: string, language: string): Promise<string[]> {
	const { order } = await svc.get_topic_roots({ version, language });
	ditarootsChange({ ditaroots: order });
	return order;
}

function show_topicsets(roots: string[]) {
	roots.forEach((rootname) => {
		logUser.debug(() => `${rootname}`);
		const tslist = childmap.get(rootname) || [];
		tslist.forEach((ts) => {
			const { title } = keymap.get(ts) || { title: '' };
			logUser.debug(() => `* ${ts} - ${title}`);
		});
	});
}

export async function load_roots(roots: string[], version: string, language: string): Promise<TopicRoots> {
	const topics = {} as TopicRoots;
	const promises: Promise<any>[] = [];
	if (roots.length === 0) {
		roots = await get_topic_roots(version, language);
	}

	roots.forEach((rootname) => {
		topics[rootname] = {};
		const one_root = async () => {
			topics[rootname] = await get_state({
				svc_call: () => svc.get_root({ rootname, version, language }),
				key: `root.${rootname}`,
				version,
				language
			});
			return calcPaths(rootname, topics[rootname]);
		};
		promises.push(one_root());
	});

	await Promise.all(promises);
	await setup_quizzes();
	show_topicsets(roots);
	topicsChange({ topics });
	setTimeout(() => {
		recommendedChange({});
	}, 100);
	cache_all_topics(version, language);
	return topics;
}

export function get_parents(version: string, language: string): Promise<ParentTree> {
	return svc.get_parenttree({ version, language });
}
